import type { RoleKey } from './domain.js';

/**
 * Permission catalogue. Roles are seeded with the defaults below, but every
 * role/permission pair is a row in the database — an administrator can re-configure
 * any of it from Settings › Roles without a code change.
 */
export const PERMISSIONS = [
  // Inventory
  { key: 'vehicles:read', group: 'Inventory', description: 'View vehicles and stock lists' },
  { key: 'vehicles:write', group: 'Inventory', description: 'Create and edit vehicle records' },
  { key: 'vehicles:move', group: 'Inventory', description: 'Record allocations, dispatches and sales' },
  { key: 'vehicles:sell', group: 'Inventory', description: 'Record the sale of stock the account itself holds' },
  /*
   * The exception lane (owner, 12 Sep 2026): a sale recorded on the bill photo
   * alone, with a written reason standing in for the customer's papers. It is
   * deliberately its own key and not part of `vehicles:sell` — that one sits in
   * BUYER, which is all 278 counter sign-ins, and the whole point of this lane
   * is that the counters cannot reach it. Kept out of READ_ONLY too, so ASM and
   * VIEWER never inherit it.
   */
  { key: 'vehicles:sell-on-bill', group: 'Inventory', description: 'Record a sale on the bill photo alone, with a written reason' },
  /*
   * The TVS book's stop-gap (owner, 23 Sep 2026: "for tvs sale a person or
   * sandhya can do sale by just pressing sale button no prices no sale
   * details is required for now i will tell you when to stop it"). Its own
   * key so it can be taken off a role, or removed, without touching the
   * ordinary sale. The server refuses it outside the TVS book.
   */
  { key: 'vehicles:quick-sell', group: 'Inventory', description: 'Mark a TVS vehicle sold with nothing else — for now' },
  /*
   * Passing a factory chassis's PDI check into the dispatch store, and nothing
   * more (25 Sep 2026, for the factory's PDI sign-in). `vehicles:move` still
   * passes a check too — the dispatch desks and the office keep doing it — but
   * it also dispatches, allocates and sells, which a factory hand must not.
   */
  { key: 'vehicles:pdi', group: 'Inventory', description: "Pass a factory chassis's PDI check into HEAD OFFICE" },
  /*
   * The production plan alone (owner, 2 Oct 2026: "Make production section
   * visible to amit and rahul as well … amit works at factory he should know
   * which vehicles to build from the orders"). The factory's PDI hand reads
   * what the orders ask for, model by model and variant by variant, against
   * the store — without the order desk, which `requests:read` would open.
   * Everyone holding `requests:read` reads the plan already.
   */
  { key: 'production:read', group: 'Network', description: 'Read the production sheet — what to build next by model, variant and colour. No orders, customers or counters: the full plan is the order desk\'s (requests:read)' },
  // A driver's own deliveries and nothing else (26 Sep 2026): read them, start them.
  { key: 'deliveries:drive', group: 'Inventory', description: 'See the vehicles assigned to you for delivery and start each delivery' },

  // Billing (14 Sep 2026) — the two desks that raise bills, and the one person
  // who confirms the money has arrived.
  { key: 'billing:dispatch', group: 'Billing', description: 'Bill a dispatch: the maker billing the company it sent the vehicles to' },
  { key: 'billing:retail', group: 'Billing', description: 'Bill an outlet or mitr for a vehicle they have sold' },
  { key: 'deposits:verify', group: 'Billing', description: 'Confirm the money has arrived and close it' },
  /*
   * The billing chain (owner, 1 Oct 2026): bill → RTO receipt → insurance
   * policy → payment received → RC and number plate given, then the sale is
   * concluded. Each step is its own key, so each person does exactly theirs;
   * `billing:desk` opens the desk to read it.
   */
  { key: 'billing:desk', group: 'Billing', description: 'Open the billing desk — every counter sale and how far along the billing chain it is' },
  { key: 'billing:rto', group: 'Billing', description: "Upload the RTO receipt against an insured sale (step 3)" },
  { key: 'billing:insurance', group: 'Billing', description: 'Upload the insurance policy against a billed sale (step 2)' },
  { key: 'billing:payment', group: 'Billing', description: "Confirm a sale's payment has been received in full — no longer a step of the billing chain (1 Oct 2026)" },
  { key: 'billing:handover', group: 'Billing', description: 'Confirm the RC and number plate have been given — the sale is concluded (step 4)' },
  /*
   * The Bright on Wheels ledger (owner, 28 Sep 2026): the office, billing and
   * the head ASMs read the whole book; a mitr or outlet reads its own sales.
   */
  { key: 'ledger:read', group: 'Billing', description: 'Read the ledger — every app sale, what is owed on it, and the missing receipts' },
  /*
   * The customer on a ledger sale (owner, 29 Sep 2026: "shanti also maintains
   * company ledger she should be able to see the sale amount as well and also
   * uploaded customer details like aadhar and everything else"). The Aadhaar
   * reading, the phone and address, and every paper the counter uploaded —
   * apart from `ledger:read`, because the ledger's money is open to the head
   * ASMs and the counters and the customer's identity papers are not.
   */
  /*
   * The activity monitor (owner, 29 Sep 2026: "i want to see how many
   * dispatches the dispatch guy did and every user activity … to check
   * whether everybody is doing their work on the app"). Every person in every
   * book and what they did — the owner's, and administrators' only.
   */
  { key: 'activity:read', group: 'Administration', description: 'See who is using the app, for how long, and the work each person did in it' },
  // The owner's flyers and Yes/No questions to the counters (1 Oct 2026).
  { key: 'announcements:manage', group: 'Administration', description: 'Post announcements and flyers to the counters, and read their Yes/No answers' },
  /*
   * The ASMs' field desk, read from above (owner, 3 Oct 2026: "i need details
   * about everything how many people they met how many times once its gets
   * converted then also a performance review we give then incentives based on
   * this"). An ASM keeps their own field desk by role; this reads every ASM's,
   * answers their expenses and gives the month's review.
   */
  { key: 'field:review', group: 'Network', description: "Read every ASM's meetings and prospects, approve every employee's expenses, and give the ASMs' monthly review and incentive" },
  { key: 'ledger:customers', group: 'Billing', description: "See the customer on a ledger sale — the Aadhaar reading, phone, address and every paper uploaded" },
  { key: 'vehicles:delete', group: 'Inventory', description: 'Archive vehicle records' },

  // Counterparties
  { key: 'parties:read', group: 'Network', description: 'View dealers, sub dealers and outlets' },
  { key: 'parties:write', group: 'Network', description: 'Create and edit network partners' },
  { key: 'asms:read', group: 'Network', description: 'View area sales managers' },
  { key: 'asms:write', group: 'Network', description: 'Create and edit area sales managers' },
  { key: 'requests:read', group: 'Network', description: 'View service calls, complaints and stock orders from the network' },
  { key: 'requests:manage', group: 'Network', description: 'Answer and close network requests' },
  /*
   * Raising a stock order, and changing one you raised.
   *
   * Split out of `vehicles:read` on 14 Sep 2026. Raising an order was gated on
   * the read key because everyone who raises one — a mitr at its counter, an
   * ASM in the field — holds nothing else, and the real rule lives in the
   * service (your own order, only while nothing is held against it). That was
   * true until the first account existed that must READ the book and write
   * nothing at all: the owner asked for Goswami to have "bright on wheels
   * complete book view only", and a view-only account that can still raise
   * orders is not view only. Held by everyone who could raise one before.
   */
  { key: 'requests:raise', group: 'Network', description: 'Raise a stock order, and change one you raised' },

  // Intelligence
  { key: 'analytics:read', group: 'Intelligence', description: 'View dashboards and analytics' },
  { key: 'reports:run', group: 'Intelligence', description: 'Generate and download reports' },
  { key: 'alerts:read', group: 'Intelligence', description: 'View alerts' },
  { key: 'alerts:manage', group: 'Intelligence', description: 'Acknowledge, resolve and mute alerts' },
  { key: 'passport:read', group: 'Intelligence', description: 'Trace a chassis across the wholesale and retail ledgers' },

  // Factory
  { key: 'factory:read', group: 'Factory', description: 'View the factory store — items and counts' },
  { key: 'factory:write', group: 'Factory', description: 'List factory items and record their counts' },
  // The painter (owner, 26 Oct 2026: "painter will select chasis and update the paint on the chasis").
  { key: 'paint:write', group: 'Factory', description: 'Record the colour a production-store chassis is painted' },
  /*
   * New chassis into the factory register by hand (owner, 28 Sep 2026: "a
   * person will manually upload new chasis when required"). A chassis added
   * this way can land in the PDI store as stock, so it is its own key, held by
   * the administrator until the owner names who else adds them.
   */
  { key: 'chassis:upload', group: 'Factory', description: 'Add new chassis to the factory register by hand (Purchase data)' },
  // The guards' gate register (23 Oct 2026): loader boxes, frames and fitments between the head office store and the two factories.
  { key: 'material:move', group: 'Factory', description: 'Send material between the head office store and the Transport Nagar and Darogakheda factories, receive what arrives, and count it' },
  // The gate's tick on every dispatched vehicle (23 Oct 2026): a job, not a hold.
  { key: 'gate:out', group: 'Factory', description: 'Vehicles in and out at the gate, with a photo of the chassis — a dispatched vehicle leaving is ticked off with it' },

  // Service desk (20 Oct 2026)
  { key: 'service:desk', group: 'Service', description: 'Work the complaints and service calls, and fit parts from the factory store against them' },

  // Synchronisation
  { key: 'sync:read', group: 'Synchronisation', description: 'View sync history and logs' },
  { key: 'sync:run', group: 'Synchronisation', description: 'Trigger a sync manually' },
  { key: 'sync:configure', group: 'Synchronisation', description: 'Configure sheet sources and schedules' },
  /*
   * BUSY's exports dropped into the TVS book by hand (owner, 23 Sep 2026).
   * Its own key, not `sync:run`: the billing desk holds it so Sandhya can
   * upload the purchases on her one sign-in, and `sync:run` would open the
   * sheet syncs to her as well.
   */
  { key: 'busy:upload', group: 'Synchronisation', description: "Upload BUSY's exports into the TVS book" },

  // Administration
  { key: 'users:read', group: 'Administration', description: 'View users' },
  { key: 'users:manage', group: 'Administration', description: 'Create, edit and deactivate users' },
  { key: 'roles:manage', group: 'Administration', description: 'Change role permissions' },
  { key: 'audit:read', group: 'Administration', description: 'View the audit trail' },
  { key: 'settings:manage', group: 'Administration', description: 'Change organisation settings' },
] as const;

export type PermissionKey = (typeof PERMISSIONS)[number]['key'];

export const ALL_PERMISSION_KEYS = PERMISSIONS.map((p) => p.key) as PermissionKey[];

const READ_ONLY: PermissionKey[] = [
  'vehicles:read',
  'parties:read',
  'asms:read',
  'analytics:read',
  'alerts:read',
  'reports:run',
  'factory:read',
];

export const DEFAULT_ROLE_PERMISSIONS: Record<RoleKey, PermissionKey[]> = {
  ADMINISTRATOR: [...ALL_PERMISSION_KEYS],
  INVENTORY_MANAGER: [
    ...READ_ONLY,
    'requests:raise',
    'vehicles:write',
    'vehicles:move',
    'parties:write',
    'asms:write',
    'factory:write',
    'requests:read',
    'requests:manage',
    'alerts:manage',
    // The cross-tier passport is a staff instrument: deliberately absent from
    // READ_ONLY so ASM and VIEWER never inherit it.
    'passport:read',
    'sync:read',
    'sync:run',
    'busy:upload',
    'vehicles:quick-sell',
    'audit:read',
  ],
  SALES_MANAGER: [
    ...READ_ONLY,
    'requests:raise',
    'vehicles:move',
    'requests:read',
    'requests:manage',
    'alerts:manage',
    'passport:read',
    'sync:read',
  ],
  // Territory-hard since 25 Aug 2026: their mitras'/dealers' stock and
  // sales only. No alerts, reports or sync — those read the whole book.
  ASM: ['vehicles:read', 'parties:read', 'asms:read', 'analytics:read', 'requests:raise', 'ledger:read'],
  VIEWER: ['vehicles:read', 'parties:read', 'analytics:read', 'alerts:read', 'requests:read'],
  // A buyer lives entirely inside the vehicles module, and every read it makes is
  // forced down to its own scopeParty server-side: the list, each profile, search,
  // the facet filters, a summary for its dashboard, and a CSV of its own vehicles.
  // `vehicles:read` plus `vehicles:sell` — the sell endpoint only ever acts on a
  // vehicle standing in the account's own hands, so the write surface is exactly
  // its own stock. It deliberately cannot reach analytics, reports, the network
  // or sync, so there is no org-wide surface for it to widen into.
  BUYER: ['vehicles:read', 'vehicles:sell', 'vehicles:quick-sell', 'requests:raise', 'ledger:read'],
  // The order desk and what it needs: the store and the dispatch form
  // (vehicles), the buyers (parties), the requests themselves.
  DISPATCH: ['vehicles:read', 'vehicles:write', 'vehicles:move', 'parties:read', 'requests:read', 'requests:manage', 'requests:raise'],
  // The factory's own store keeper (9 Sep 2026): the item list and the counts,
  // and nothing else — no vehicles, no network, no sync.
  FACTORY: ['factory:read', 'factory:write'],
  // The factory's PDI hand (25 Sep 2026): read the vehicles, pass the checks.
  // No move, no write, no orders, no network. Since 2 Oct 2026 also what to
  // build: the production plan, and nothing else of the order desk. Since
  // 21 Oct 2026 the factory store too: he decides the counts the shelf and the
  // app disagree on, with the owner (`approvesFactory`).
  FACTORY_PDI: ['vehicles:read', 'vehicles:pdi', 'production:read', 'factory:read'],
  // The TVS dispatch hand (6 Oct 2026): the TVS dispatch and what it reads — no sale, no billing, no orders.
  TVS_DISPATCH: ['vehicles:read', 'vehicles:move'],
  // A driver reads only the deliveries assigned to them — no stock list, no search (26 Sep 2026).
  DRIVER: ['deliveries:drive'],
  // The telecalling team (13 Oct 2026): the enquiry book by its role, and the vehicles read to say
  // what is in stock. Nothing sold, moved, ordered or billed.
  TELECALLER: ['vehicles:read'],
  // Staff (14 Oct 2026): nothing. Attendance, tasks and expenses are every employee's without a grant.
  STAFF: [],
  // The complaints of the three books the factory makes for, and the parts on the shelf to fit (20 Oct 2026).
  SERVICE: ['service:desk', 'factory:read'],
  // The guards (23 Oct 2026): material between the head office store and the two factories, and nothing else.
  // The guards (24 Oct 2026): vehicles in and out at their gate. Material is one guard's alone — the
  // `movesMaterial` switch on his sign-in (Akhilesh); the role's right is needed as well, and is not enough.
  GUARD: ['material:move', 'gate:out'],
  STORE_GUARD: ['material:move', 'gate:out'],
  // The painter (26 Oct 2026): the production store's chassis and the colour each is painted — nothing else.
  PAINTER: ['paint:write'],
  /*
   * The billing desk reads the book and raises bills against what it finds —
   * and nothing else. No selling, no moving, no archiving, no users, no roles.
   * Confirming the money is a separate key it does NOT hold: whoever raises the
   * bill should not also be the one who says it was paid.
   */
  BILLING: ['vehicles:read', 'parties:read', 'requests:read', 'billing:dispatch', 'billing:retail', 'billing:desk', 'requests:raise', 'busy:upload', 'vehicles:quick-sell', 'ledger:read', 'ledger:customers'],
  // The chain after the bill (1 Oct 2026): read the desk, do the one step.
  RTO_DESK: ['vehicles:read', 'parties:read', 'billing:desk', 'billing:rto'],
  INSURANCE_DESK: ['vehicles:read', 'parties:read', 'billing:desk', 'billing:insurance'],
  RC_DESK: ['vehicles:read', 'parties:read', 'billing:desk', 'billing:handover'],
  /*
   * The distributor's store desk: a counter that also dispatches.
   *
   * The counter's own rights — read its stock, sell from it, keep the enquiry
   * book — plus what sending stock on requires: read the orders, answer them,
   * move a vehicle. It does NOT hold `requests:raise`: the store has nobody to
   * order from, it IS the store (15 Sep 2026).
   */
  /**
   * The sales head (owner, 16 Sep 2026: "manish portal needs some changes first
   * he should have all the access same as goswami and also he should also have
   * dispatched section inside like neeraj he needs to read that data").
   *
   * Everything an area sales manager holds, over the WHOLE book rather than one
   * territory — that is what Goswami has, and it comes from carrying no `asmId`
   * rather than from the role — plus `requests:read`, which is what opens the
   * dispatched register and the maker's dispatches into this book.
   *
   * Its own role rather than widening ASM: eight people hold ASM, and giving
   * all of them the desk's view of every order in every book is not what was
   * asked for.
   */
  SALES_HEAD: [
    'analytics:read',
    'asms:read',
    'ledger:read',
    'parties:read',
    'requests:raise',
    'requests:read',
    'vehicles:read',
  ],
  STORE_DESK: [
    'vehicles:read',
    'vehicles:sell',
    'vehicles:quick-sell',
    'vehicles:move',
    'parties:read',
    'requests:read',
    'requests:manage',
    // Orders for its own store's stock (owner, 18 Sep 2026: "give vivek sales
    // right as well to place orders").
    'requests:raise',
  ],
};

export const ROLE_LABELS: Record<RoleKey, string> = {
  ADMINISTRATOR: 'Administrator',
  INVENTORY_MANAGER: 'Inventory manager',
  SALES_MANAGER: 'Sales manager',
  ASM: 'Area sales manager',
  VIEWER: 'Viewer',
  BUYER: 'Buyer portal',
  DISPATCH: 'Dispatch desk',
  FACTORY: 'Factory store',
  FACTORY_PDI: 'Factory PDI',
  TVS_DISPATCH: 'TVS dispatch',
  TELECALLER: 'Telecalling',
  STAFF: 'Staff',
  SERVICE: 'Service desk',
  GUARD: 'Guard',
  STORE_GUARD: 'Store guard',
  PAINTER: 'Painter',
  DRIVER: 'Driver',
  BILLING: 'Billing desk',
  RTO_DESK: 'RTO desk',
  INSURANCE_DESK: 'Insurance desk',
  RC_DESK: 'RC & plate desk',
  SALES_HEAD: 'Sales head',
  STORE_DESK: 'Store desk',
};

export const ROLE_DESCRIPTIONS: Record<RoleKey, string> = {
  ADMINISTRATOR: 'Full access, including users, roles and sync configuration.',
  INVENTORY_MANAGER: 'Runs day-to-day stock: movements, network, syncs and reports.',
  SALES_MANAGER: 'Tracks dispatches and dealer performance, records sales.',
  ASM: 'Sees only their own territory — their mitras and dealers, their stock and sales.',
  VIEWER: 'Read-only access to stock and dashboards.',
  BUYER: 'A buyer signing in to see only the vehicles they were sold.',
  DISPATCH: 'Takes the network’s stock orders through to dispatch: the order desk, the store and the dispatch form.',
  FACTORY: 'Lists what the factory holds and keeps the counts right. Nothing else.',
  FACTORY_PDI: 'Checks the chassis the factory sends and passes them into HEAD OFFICE. Reads vehicles; cannot dispatch, sell or move stock.',
  TVS_DISPATCH: 'Dispatches TVS vehicles from the TVS store to the mitras and outlets, with the plate photos. Nothing else.',
  TELECALLER: "Calls the owner's lead sheets: takes a lead from the shared pool, calls it, and writes what came of every call. Sees only the leads not yet taken and their own; sells, moves and orders nothing.",
  DRIVER: 'Sees only the vehicles assigned to them for delivery, and starts each delivery. The counter marks the handover.',
  STAFF: 'Marks their own attendance, does the tasks the owner gives them, and adds their expenses. Nothing else — no stock, sales, orders, parties or customers.',
  SERVICE: 'Works every complaint and service call of Bright Autozone, Bright on Wheels and FAB EV, walk-ins too, and fits parts from the factory store against them — warranty or paid, the old part back to the faulty bin.',
  GUARD: 'A guard at a factory gate: every vehicle in and out, with a photo of its chassis, and his attendance. Material between the store and the factories only when switched on for him in Users. Nothing else.',
  PAINTER: 'The paint shop: picks a chassis in the production store and records the colour it is painted, before the factory store gives it its motor, controller and differential. Nothing else.',
  STORE_GUARD: 'A guard at the head office store gate: every vehicle in and out, with a photo of its chassis — a dispatched vehicle leaving is ticked off with it — and his attendance. Material between the store and the factories only when switched on for him in Users. Nothing else.',
  BILLING: 'Raises the bill against a dispatch or a sale and uploads it. Cannot sell, move stock, or confirm money.',
  RTO_DESK: 'Step 3 of the billing chain: uploads the RTO receipt once the sale is billed and insured.',
  INSURANCE_DESK: 'Step 2 of the billing chain: uploads the insurance policy against each billed sale.',
  RC_DESK: 'Step 4 of the billing chain: confirms the RC and number plate have reached the customer, which concludes the sale.',
  SALES_HEAD: 'The whole book, not one territory: raises orders, and reads the dispatched register.',
  STORE_DESK: 'The distributor’s own store: sells over the counter, and sends stock on to its mitras and outlets.',
};
